import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'cursor-pointer inline-flex items-center justify-center rounded font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline: 'border border-border hover:bg-secondary',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'underline-offset-4 hover:underline text-primary',
			},
			size: {
				default: 'min-h-10 min-w-[60px] py-2 px-4',
				sm: 'min-h-9 min-w-[60px] px-3 rounded',
				lg: 'min-h-11 min-w-[60px] px-8 rounded',
				icon: 'h-8 w-8 rounded flex items-center justify-center',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

const Button = React.forwardRef(function Button(
	{
		className,
		variant = 'default',
		size,
		asChild = false,
		loading = false,
		disabled,
		fullWidth = false,
		children,
		...props
	},
	ref,
) {
	const Comp = asChild ? Slot : 'button';
	const isDisabled = Boolean(disabled || loading);

	const spinnerClassName = variant === 'default' ? 'border-primary-foreground border-t-transparent' : 'border-primary';

	const spinner = loading ? (
		<span className="flex justify-center items-center">
			<span className={cn('w-4 h-4 border-2 border-t-transparent rounded-full animate-spin me-2', spinnerClassName)} />
		</span>
	) : null;

	const mergedClassName = cn(buttonVariants({ variant, size, className }), fullWidth && 'w-full');

	if (asChild && loading) {
		const child = React.Children.only(children);
		return (
			<Comp
				data-slot="button"
				data-variant={variant}
				data-size={size}
				className={mergedClassName}
				disabled={isDisabled || undefined}
				aria-disabled={isDisabled || undefined}
				ref={ref}
				{...props}
			>
				{React.cloneElement(
					child,
					undefined,
					<>
						{spinner}
						{child.props.children}
					</>,
				)}
			</Comp>
		);
	}

	return (
		<Comp
			data-slot="button"
			data-variant={variant}
			data-size={size}
			className={mergedClassName}
			disabled={isDisabled || undefined}
			aria-disabled={isDisabled || undefined}
			ref={ref}
			{...props}
		>
			{spinner}
			{children}
		</Comp>
	);
});

export { Button, buttonVariants };
